/**
 * Vidéos de visite : montage côté serveur (FileMontage), comme dans l'app.
 * Sur ordinateur on n'a pas la caméra : la source est un lot de photos ou une
 * vidéo déjà tournée, déposée telle quelle sur /videos/visite.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

const API = '/api/videos';
function jeton() { return localStorage.getItem('authToken') || ''; }
function h() { return { Authorization: `Bearer ${jeton()}` }; }
function hJson() { return { ...h(), 'Content-Type': 'application/json' }; }

interface Modele { id: string; nom: string; resume: string; couleur: string; affiche: string | null; apercu: string | null; familleOrdre?: number }
interface Musique { id: string; nom: string; ambiance: string; extrait?: string | null }
interface Style { id: string; nom: string; resume: string }
interface Rythme { id: string; nom: string }
interface Travail {
  id: string; etat: 'en_attente' | 'en_cours' | 'termine' | 'echec'; progression: number; etape: string; erreur?: string | null;
  type?: string; cree?: string; ville?: string; prix?: string; titre?: string; type_bien?: string; surface?: string;
  resultat?: { video?: string; images?: string[]; duree?: number } | null;
}

const VIDE = { titre: '', type: 'Maison', ville: '', prix: '', surface: '', pieces: '', chambres: '' };

/** Les images de modèles sont derrière le jeton : une balise img nue recevrait un 401. */
function MediaProtege({ src, video, className }: { src: string; video?: boolean; className?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let annule = false;
    let cree: string | null = null;
    fetch(src, { headers: h() }).then(r => (r.ok ? r.blob() : null)).then(b => {
      if (!b || annule) return;
      cree = URL.createObjectURL(b);
      setUrl(cree);
    }).catch(() => { /* la carte reste sans image */ });
    return () => { annule = true; if (cree) URL.revokeObjectURL(cree); };
  }, [src]);
  if (!url) return <div className={`bg-gray-100 ${className || ''}`} />;
  return video
    ? <video src={url} autoPlay loop muted playsInline className={className} />
    : <img src={url} alt="" className={className} />;
}

function envoyerAvecProgression(url: string, corps: FormData, surProgres: (p: number) => void) {
  return new Promise<any>((ok, ko) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${jeton()}`);
    xhr.upload.onprogress = e => { if (e.lengthComputable) surProgres(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      let d: any = {};
      try { d = JSON.parse(xhr.responseText); } catch { /* réponse non JSON du proxy (fichier trop lourd, par exemple) */ }
      if (xhr.status >= 200 && xhr.status < 300) ok(d);
      else ko(new Error(d.message || "L'envoi a échoué"));
    };
    xhr.onerror = () => ko(new Error("L'envoi a échoué, vérifiez votre connexion"));
    xhr.send(corps);
  });
}

export default function VideosPage() {
  const [modeles, setModeles] = useState<Modele[]>([]);
  const [musiques, setMusiques] = useState<Musique[]>([]);
  const [styles, setStyles] = useState<Style[]>([]);
  const [rythmes, setRythmes] = useState<Rythme[]>([]);
  const [travaux, setTravaux] = useState<Travail[]>([]);
  const [source, setSource] = useState<'photos' | 'video'>('photos');
  const [photos, setPhotos] = useState<File[]>([]);
  const [video, setVideo] = useState<File | null>(null);
  const [modele, setModele] = useState('');
  const [survol, setSurvol] = useState<string | null>(null);
  const [musique, setMusique] = useState('auto');
  const [style, setStyle] = useState('auto');
  const [rythme, setRythme] = useState('normal');
  const [bien, setBien] = useState(VIDE);
  const [progres, setProgres] = useState<number | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [edition, setEdition] = useState<string | null>(null);
  const [champs, setChamps] = useState(VIDE);
  const [occupe, setOccupe] = useState<string | null>(null);
  const [depot, setDepot] = useState(false);
  const [vignettes, setVignettes] = useState<string[]>([]);
  const champPhotos = useRef<HTMLInputElement>(null);
  const champVideo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const urls = photos.map(p => URL.createObjectURL(p));
    setVignettes(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [photos]);

  // Ajoute au lot au lieu de le remplacer : on peut piocher dans plusieurs
  // dossiers sans perdre le premier choix.
  const ajouterPhotos = (liste: File[]) => {
    const images = liste.filter(f => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name));
    if (images.length < liste.length) setErreur('Seules les images sont acceptées ici.');
    setPhotos(p => [...p, ...images].slice(0, 20));
  };

  const deposer = (e: React.DragEvent) => {
    e.preventDefault();
    setDepot(false);
    const fichiers = Array.from(e.dataTransfer.files);
    if (source === 'photos') { ajouterPhotos(fichiers); return; }
    const v = fichiers.find(f => f.type.startsWith('video/'));
    if (v) setVideo(v); else setErreur('Déposez un fichier vidéo.');
  };

  // Le clic sur l'onglet ouvre directement le sélecteur du système : Finder ou
  // Photos sur Mac, Explorateur sur PC. Il doit partir du clic lui-même, sinon
  // le navigateur bloque l'ouverture.
  const choisirSource = (s: 'photos' | 'video') => {
    setSource(s);
    (s === 'photos' ? champPhotos : champVideo).current?.click();
  };

  const chargerTravaux = useCallback(async () => {
    const r = await fetch(`${API}/travaux`, { headers: h() });
    if (r.ok) setTravaux((await r.json()).travaux || []);
  }, []);

  useEffect(() => {
    const lire = (u: string) => fetch(u, { headers: h() }).then(r => (r.ok ? r.json() : {})).catch(() => ({}));
    lire(`${API}/modeles`).then((d: any) => setModeles(d.modeles || []));
    lire(`${API}/musiques`).then((d: any) => setMusiques(d.musiques || []));
    lire(`${API}/styles`).then((d: any) => { setStyles(d.styles || []); setRythmes(d.rythmes || []); });
    chargerTravaux().catch(() => null);
  }, [chargerTravaux]);

  // Le montage peut prendre plusieurs minutes : on suit tant qu'il reste un
  // travail en cours, et on s'arrête ensuite.
  const enCours = travaux.some(t => t.etat === 'en_attente' || t.etat === 'en_cours');
  useEffect(() => {
    if (!enCours) return;
    const minuteur = setInterval(() => { chargerTravaux().catch(() => null); }, 4000);
    return () => clearInterval(minuteur);
  }, [enCours, chargerTravaux]);

  const lancer = async () => {
    setErreur(null);
    setInfo(null);
    if (source === 'photos' && photos.length < 3) { setErreur('Choisissez au moins trois photos.'); return; }
    if (source === 'video' && !video) { setErreur('Choisissez une vidéo.'); return; }

    const corps = new FormData();
    Object.entries(bien).forEach(([k, v]) => { if (v) corps.append(k, v); });
    corps.append('musique', musique);
    if (source === 'photos') {
      corps.append('modele', modele || 'auto');
      photos.forEach(p => corps.append('photos', p));
    } else {
      corps.append('style', style);
      corps.append('rythme', rythme);
      corps.append('video', video as File);
    }

    setProgres(0);
    try {
      await envoyerAvecProgression(`${API}/${source === 'photos' ? 'photos' : 'visite'}`, corps, setProgres);
      setPhotos([]);
      setVideo(null);
      setBien(VIDE);
      setInfo('Montage lancé : vous pouvez quitter cette page, il continue sur le serveur.');
      await chargerTravaux();
    } catch (e: any) {
      setErreur(e.message);
    } finally {
      setProgres(null);
    }
  };

  const publier = async (t: Travail) => {
    setOccupe(t.id);
    setErreur(null);
    try {
      const r = await fetch(`${API}/travaux/${t.id}/publier`, { method: 'POST', headers: hJson(), body: JSON.stringify({ type: t.type_bien, prix: t.prix, ville: t.ville, surface: t.surface }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Publication impossible');
      setInfo(`Vidéo programmée : ${d.publications} publication${d.publications > 1 ? 's' : ''}.`);
    } catch (e: any) { setErreur(e.message); } finally { setOccupe(null); }
  };

  const corriger = async (t: Travail) => {
    setOccupe(t.id);
    setErreur(null);
    try {
      const r = await fetch(`${API}/travaux/${t.id}`, { method: 'PUT', headers: hJson(), body: JSON.stringify(champs) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Correction impossible');
      setEdition(null);
      await chargerTravaux();
    } catch (e: any) { setErreur(e.message); } finally { setOccupe(null); }
  };

  const supprimer = async (t: Travail) => {
    if (!window.confirm('Supprimer cette vidéo ?')) return;
    await fetch(`${API}/travaux/${t.id}`, { method: 'DELETE', headers: h() }).catch(() => null);
    chargerTravaux().catch(() => null);
  };

  const copierLien = async (t: Travail) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${t.resultat?.video}`);
      setInfo('Lien copié.');
    } catch { setErreur('Copie refusée par le navigateur.'); }
  };

  const champ = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm';
  const bouton = 'px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50';

  const formulaireBien = (v: typeof VIDE, maj: (c: typeof VIDE) => void) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      <input className={`${champ} col-span-2 sm:col-span-3`} maxLength={42} placeholder="Titre (facultatif)" value={v.titre} onChange={e => maj({ ...v, titre: e.target.value })} />
      <input className={champ} placeholder="Type de bien" value={v.type} onChange={e => maj({ ...v, type: e.target.value })} />
      <input className={champ} placeholder="Ville" value={v.ville} onChange={e => maj({ ...v, ville: e.target.value })} />
      <input className={champ} placeholder="Prix" value={v.prix} onChange={e => maj({ ...v, prix: e.target.value })} />
      <input className={champ} placeholder="Surface" value={v.surface} onChange={e => maj({ ...v, surface: e.target.value })} />
      <input className={champ} placeholder="Pièces" value={v.pieces} onChange={e => maj({ ...v, pieces: e.target.value })} />
      <input className={champ} placeholder="Chambres" value={v.chambres} onChange={e => maj({ ...v, chambres: e.target.value })} />
    </div>
  );

  const extrait = musiques.find(x => x.id === musique)?.extrait;

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {erreur && <p className="text-sm text-red-600">{erreur}</p>}
      {info && <p className="text-sm text-emerald-700">{info}</p>}

      <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
        <div className="flex gap-2">
          <button onClick={() => choisirSource('photos')} className={`px-4 py-2 text-sm rounded-lg ${source === 'photos' ? 'bg-violet-600 text-white' : 'border border-gray-200'}`}>Depuis mes photos</button>
          <button onClick={() => choisirSource('video')} className={`px-4 py-2 text-sm rounded-lg ${source === 'video' ? 'bg-violet-600 text-white' : 'border border-gray-200'}`}>Depuis une vidéo</button>
        </div>

        <input ref={champPhotos} type="file" accept="image/*,.heic,.heif" multiple className="hidden"
          onChange={e => { ajouterPhotos(Array.from(e.target.files || [])); e.target.value = ''; }} />
        <input ref={champVideo} type="file" accept="video/*" className="hidden"
          onChange={e => { setVideo(e.target.files?.[0] || null); e.target.value = ''; }} />

        <button type="button" onClick={() => (source === 'photos' ? champPhotos : champVideo).current?.click()}
          onDragOver={e => { e.preventDefault(); setDepot(true); }} onDragLeave={() => setDepot(false)} onDrop={deposer}
          className={`w-full border-2 border-dashed rounded-xl py-8 px-4 text-center ${depot ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:border-violet-400'}`}>
          <p className="text-sm font-semibold text-gray-800">
            {source === 'photos'
              ? (photos.length ? `${photos.length} photo${photos.length > 1 ? 's' : ''} choisie${photos.length > 1 ? 's' : ''} : ajouter d'autres photos` : 'Choisir mes photos sur cet ordinateur')
              : (video ? video.name : 'Choisir ma vidéo sur cet ordinateur')}
          </p>
          <p className="text-xs text-gray-500 mt-1">{source === 'photos' ? 'Ou glissez-les ici. De 3 à 20 photos.' : 'Ou glissez-la ici.'}</p>
        </button>

        {source === 'photos' && vignettes.length > 0 && (
          <div className="grid grid-cols-5 sm:grid-cols-8 gap-2">
            {vignettes.map((u, i) => (
              <div key={u} className="relative">
                <img src={u} alt="" className="w-full aspect-square object-cover rounded-lg bg-gray-100" />
                <button type="button" aria-label="Retirer cette photo" onClick={() => setPhotos(l => l.filter((_, n) => n !== i))}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-900 text-white text-xs leading-none">×</button>
              </div>
            ))}
          </div>
        )}

        {source === 'video' && (
          <div className="grid sm:grid-cols-2 gap-2">
            <select className={champ} value={style} onChange={e => setStyle(e.target.value)}>{styles.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}</select>
            <select className={champ} value={rythme} onChange={e => setRythme(e.target.value)}>{rythmes.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}</select>
          </div>
        )}

        {source === 'photos' && modeles.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {[...modeles].sort((a, b) => (a.familleOrdre ?? 99) - (b.familleOrdre ?? 99)).map(m => (
              <button key={m.id} onClick={() => setModele(m.id)} onMouseEnter={() => setSurvol(m.id)} onMouseLeave={() => setSurvol(null)}
                title={m.resume} className={`text-left rounded-lg overflow-hidden border-2 ${modele === m.id ? 'border-violet-600' : 'border-transparent'}`}>
                {survol === m.id && m.apercu
                  ? <MediaProtege src={m.apercu} video className="w-full aspect-[9/16] object-cover" />
                  : m.affiche
                    ? <MediaProtege src={m.affiche} className="w-full aspect-[9/16] object-cover" />
                    : <div className="w-full aspect-[9/16]" style={{ background: m.couleur }} />}
                <p className="text-xs font-semibold text-gray-800 p-1.5">{m.nom}</p>
              </button>
            ))}
          </div>
        )}

        <select className={champ} value={musique} onChange={e => setMusique(e.target.value)}>
          <option value="auto">Musique automatique</option>
          {musiques.filter(m => m.id !== 'auto').map(m => <option key={m.id} value={m.id}>{m.nom}{m.ambiance ? ` · ${m.ambiance}` : ''}</option>)}
        </select>
        {extrait && <audio key={musique} controls src={extrait} className="w-full h-9" />}

        {formulaireBien(bien, setBien)}

        <button disabled={progres !== null} onClick={lancer} className="px-5 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-xl hover:bg-violet-700 disabled:opacity-60">
          {progres !== null ? `Envoi… ${progres} %` : 'Lancer le montage'}
        </button>
      </div>

      <h3 className="font-semibold text-gray-900">Mes vidéos</h3>
      {travaux.length === 0 && <p className="text-sm text-gray-500">Aucune vidéo pour le moment.</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        {travaux.map(t => (
          <div key={t.id} className="bg-white rounded-xl border border-gray-100 p-3 space-y-2">
            <p className="text-sm font-semibold text-gray-900">{t.titre || [t.type_bien, t.ville].filter(Boolean).join(' · ') || 'Visite'}</p>
            {(t.etat === 'en_attente' || t.etat === 'en_cours') && (
              <div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-violet-600 transition-all" style={{ width: `${t.progression || 0}%` }} /></div>
                <p className="text-xs text-gray-500 mt-1">{t.etape || 'En attente…'}</p>
              </div>
            )}
            {t.etat === 'echec' && <p className="text-sm text-red-600">{t.erreur || 'Le montage a échoué.'}</p>}
            {t.etat === 'termine' && t.resultat?.video && (
              <>
                <video controls preload="metadata" src={t.resultat.video} className="w-full max-h-96 rounded-lg bg-black" />
                <div className="flex flex-wrap gap-2">
                  <button disabled={occupe === t.id} onClick={() => publier(t)} className="px-3 py-1.5 text-sm rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">Publier</button>
                  <a href={t.resultat.video} download className={bouton}>Télécharger</a>
                  <button onClick={() => copierLien(t)} className={bouton}>Copier le lien</button>
                  <button onClick={() => {
                    setEdition(edition === t.id ? null : t.id);
                    setChamps({ ...VIDE, titre: t.titre || '', type: t.type_bien || '', ville: t.ville || '', prix: t.prix || '', surface: t.surface || '' });
                  }} className={bouton}>Corriger</button>
                </div>
                {edition === t.id && (
                  <div className="space-y-2">
                    {formulaireBien(champs, setChamps)}
                    <button disabled={occupe === t.id} onClick={() => corriger(t)} className={bouton}>Refaire le montage</button>
                  </div>
                )}
              </>
            )}
            {t.etat !== 'en_cours' && t.etat !== 'en_attente' && <button onClick={() => supprimer(t)} className="text-xs text-red-500 hover:text-red-700">Supprimer</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
